/* eslint-disable prefer-promise-reject-errors */
/* eslint-disable class-methods-use-this */
import Rx from 'rx';
import _ from 'underscore';
import moment from 'moment';
import { APP } from '../config';

import ssFetch from './ssFetch';
import SimpleCache from './SimpleCache';

import welcomeNotificationModule from './welcomeNotificationModule';
import store from '../store';
import analytics from './analytics';
import stateManager from './user/stateManager';

import { addStaticNotifications } from '../components/provider/notifications/StaticNotifications';

import {
  getFromNowDisplay,
  createNotificationParams,
  trackNotificationClick,
} from './notificationModule';

const {
  getState,
} = store;

const CACHE_FEED_SECONDS = 10;
export const LAST_VIEWED_NOTIFICATION_STATE_KEY = 'last-viewed-pro-notification';
const LAST_TRACKED_STATE_KEY = 'last-tracked-pro-notification';

// How often notifications should be checked
export const APP_REFRESH_MS = 1000 * 60; // 1 min
export const BROWSER_REFRESH_MS = 1000 * 60; // 1 min

class ProNotifications {
  stateService = null;

  cache = new SimpleCache(CACHE_FEED_SECONDS);

  notificationsStream = new Rx.BehaviorSubject();

  /**
   * RX subject used to be notified when notifications have been viewed.
   * @type {Rx.Subject}
   */
  notificationsViewedSubject = new Rx.Subject();

  /**
   * Return how frequently code should poll the notification API.
   *
   * @return {Number} Milliseconds
   */
  refreshTime() {
    return APP ? APP_REFRESH_MS : BROWSER_REFRESH_MS;
  }

  /**
   * Get all of a pro's notifications
   * @param   {Integer} providerId    The provider id
   * @param   {Object} options        Options object
   *   cached        Whether to allow cached results or not
   *   notify        Whether to notify listeners
   * @returns {Promise}
   */
  getAll(providerId, options = {}) {
    const defaultOpts = { cached: true, notify: true };
    const opts = { ...defaultOpts, ...options };
    // `return_ncd` was added because a FIRE bug was introduced in FLY-3396 that
    // necessitated a BE hotfix. The fix required all appointment notifications
    // to have the property `is_new_client_delivery: false` whether or not it was.
    // The param `return_ncd` will allow the BE to know that the bug has been fixed
    // and the FE will accept correct data again.
    // `return_pro_cancelled_appts` was added because a bug (FLY-3484) was introduced
    // in FLY-3416 that necessitated a BE hotfix. The fix required that appointment notifications
    // of the type "appointment_cancelled_provider" or "appointment_cancelled_provider_no_show"
    // be filtered out, and that appointment notifications of the type "client_appt_cancel" have
    // the "is_ncd" value set to false in order to avoid showing notifications related to the
    // appointments that are not changed an NCD fee.
    return ssFetch(
      `/api/v2/providers/${providerId}/notifications?return_ncd=true&return_pro_cancelled_appts=true`,
      { ssCache: opts.cached ? this.cache : false },
    )
      .then(res => res.json())
      .then(response => Promise.resolve(response))
      .then(response => addStaticNotifications(response, providerId))
      .then(notifications => welcomeNotificationModule.addWelcome(notifications, providerId))
      .then(notifications => {
        if (opts.notify) {
          this.notificationsStream.onNext(notifications);
        }

        if (opts.trackNotifications) {
          this.trackNotifications(notifications);
        }

        return notifications;
      });
  }

  /**
   * Filters the passed in notifications list to return a list containing only non persistent
   * notifications (aka, transient notifications).
   * @param notifications notifications to filter.
   * @returns {Array} input notifications minus persistent ones.
   */
  getTransientNotifications(notifications) {
    return _.reject(
      notifications,
      notification => notification?.persistent === true,
    );
  }

  /**
   * Returns the number of persistent notifications in the parameters that are persistent and have
   * not been already viewed. Persistent notifications are counted as seen if they have set a key
   * in the user state called `last_seen_notification_${notification.type}`.
   * @param notifications Notifications to search through.
   * @returns {Promise} Resolves to the number of non-viewed persistent notifications.
   */
  getNewPersistentNotificationCount(notifications) {
    const persistentKeys = notifications.filter(notification => notification.persistent)
      .map(notification => `last_seen_notification_${notification.type}`);

    return stateManager.getActionValues(...persistentKeys)
      .then(
        lastSeenTimes => {
          const lastSeenTimesCount = _.values(lastSeenTimes).filter(value => value).length;
          return persistentKeys.length - lastSeenTimesCount;
        },
      );
  }

  /**
   * Get the number of new notifications in the feed.
   * In this context, that means all the notifications that have been created since the last time
   * the user has seen the notification feed. It works in conjunction with setLastNotificationSeen
   * and has special considerations for "persistent" notifications that store their creation dates
   * in the User State. Persistent notifications have a "persistent=true" attribute and store the
   * date they were first seen in the user state via the setPersistentNotificationsSeen method.
   * Only persistent notifications that haven't been seen count towards this.
   *
   * @param {Number} providerId - The provider to get the notification count for.
   * @param {Array} notifications - (optional) If passed, it will use this list instead
   *                                of requesting notifications from the server.
   * @returns {Promise}
   */
  getNewNotificationCount(providerId, notificationList = null) {
    let notificationsPromise = Promise.resolve(notificationList);
    if (!Array.isArray(notificationList) || !notificationList.length) {
      notificationsPromise = this.getAll(providerId, { notify: false });
    }

    return Promise.all([
      notificationsPromise,
      stateManager.getActionValue(LAST_VIEWED_NOTIFICATION_STATE_KEY),
    ])
      .then(([notifications, lastViewedNotificationCreationTimestamp]) => {
        const transientNotifications = this.getTransientNotifications(notifications);
        return this.getNewPersistentNotificationCount(notifications)
          .then(newPersistentNotificationCount => {
            // haven't seen any notifications, all must be new.
            if (!lastViewedNotificationCreationTimestamp) {
              return newPersistentNotificationCount + transientNotifications.length;
            }
            const lastViewedNotificationCreation = moment(lastViewedNotificationCreationTimestamp);

            // Notifications are sorted by descending date. The index of the first notification
            // created before the last view date maps corresponds to the number of unseen
            // notifications.
            const lastViewedIndex = transientNotifications.findIndex(
              notification => moment(notification.creation_time) <= lastViewedNotificationCreation,
            );

            // All notifications are newer than the last one we saw.
            if (lastViewedIndex === -1) {
              return newPersistentNotificationCount + transientNotifications.length;
            }

            return newPersistentNotificationCount + lastViewedIndex;
          });
      });
  }

  /**
   * Remembers that the persistent notifications in the passed argument have been seen. For this
   * to work, the notification must have a `type` attribute, that will be used to compose a
   * key used to store the time in the user state. E.g., if a persistent notification has a
   * type of "monthly_goal" and is present in the passed in notifications, the current date will
   * be set as a user state value under the `last_seen_notification_monthly_goal`.
   * @param {Array} notifications
   * @return {Promise} Promise that resolves when seen dates for all candidate notifications are
   * stored in user state.
   */
  setPersistentNotificationsSeen(notifications) {
    const nowTimestamp = moment().toISOString();
    const promises = [];
    const currentUserProviderId = getState().user.provider_id;

    notifications.forEach(
      notification => {
        // notification.providerId is sometimes notifications.provider_id, depending on the
        // language that generated the notification (Python BE or JS FE)
        const userOwnsNotifications = Number(
          notification.providerId || notification.provider_id,
        ) === currentUserProviderId;
        if (
          notification.persistent
          && notification.type
          && userOwnsNotifications
        ) {
          const seenKey = `last_seen_notification_${notification.type}`;
          promises.push(stateManager.getActionValue(seenKey)
            .then(firstSeen => {
              if (!firstSeen) {
                promises.push(stateManager.addAction(seenKey, nowTimestamp));
              }
            }));
        }
      },
      [],
    );

    return Promise.all(promises);
  }

  /**
   * Mark the the notifications in the list as "seen". This is accomplished by finding the latest
   * non-persistent notification (aka those created by the backend which will expire after some
   * time in the stream) and remembering its creation time. Any transient notification created on or
   * after that date will be considered new next time we check in getNewNotificationCount.
   * If the provider_id in the latest notification does not match the primary
   * provider ID on the logged in user, the notifications will not be marked (to prevent admins
   * impersonating pros from remembering/clearing notifications not meant for them).
   *
   * @param {Array} notifications   Ordered list of notifications
   * @return {Promise}
   */
  setLastNotficationSeen(notifications) {
    if (!notifications || !notifications.length) {
      return Promise.reject('No notifications were passed.');
    }

    const transientNotifications = this.getTransientNotifications(notifications);

    if (transientNotifications.length === 0) {
      return Promise.reject('No non-persistent notifications were passed.');
    }

    // We will be storing the creation time of the latest one
    // (notifications are sorted descending order)
    const latestTransientNotification = transientNotifications[0];
    const providerId = latestTransientNotification.provider_id;
    const currentUser = getState().user;

    // Only update if the notifications are for the logged in pro.
    // i.e. don't mark as viewed for super or admin users.
    if (providerId && currentUser.providerId === Number(providerId)) {
      // Remember the creation time of the last transient notification in user state.
      return stateManager
        .addAction(LAST_VIEWED_NOTIFICATION_STATE_KEY, latestTransientNotification.creation_time)
        .then(() => { this.notificationsViewedSubject.onNext(0); });
    }

    return Promise.reject(`The logged in provider ID ${currentUser.provider_id} does not match: ${providerId}.`);
  }

  /**
   * Get relative display time
   * @param   {String} time Date string
   * @returns {String}      Time relative to now
   */
  getFromNowDisplay(time) {
    return getFromNowDisplay(time);
  }

  /**
   * Tracks delivered notifications
   * @param   {Array} notifications - List of notifications
   * @returns {undefined}
   */
  trackNotifications(notifications) {
    if (notifications.length > 0) {
      // Grab date for last notification tracked
      stateManager.getActionValue(LAST_TRACKED_STATE_KEY)
        .then(lastTrackedNotificationDate => {
          // Exit if no new untracked notifications
          if (notifications[0].creation_time === lastTrackedNotificationDate) {
            return;
          }
          // Track notifications created after the last tracked date / time, or all notifications
          // if there is no stored last notification tracked date / time.  Skip
          // welcome notifications
          notifications.forEach(notification => {
            if (this.shouldTrackNotification(notification, lastTrackedNotificationDate)) {
              const trackParams = this.createNotificationParams(notification);
              analytics.track('pro_notification_sent', {
                ...trackParams,
              });
            }
          });
          // Set last tracked notification date / time
          stateManager.addAction(LAST_TRACKED_STATE_KEY, notifications[0].creation_time);
        });
    }
  }

  /*
   * Track notifications created after the last tracked date / time, or all notifications
   * if there is no stored last notification tracked date / time.  Skip
   * welcome notifications
   *
   * @param {Object} notification - notification object
   * @param {string} lastTrackedNotificationDate - date / time of last notification tracked
   */
  shouldTrackNotification(notification, lastTrackedNotificationDate) {
    return (notification.type !== 'welcome'
      && ((lastTrackedNotificationDate
        && notification.creation_time > lastTrackedNotificationDate)
        || !lastTrackedNotificationDate)
    );
  }

  /**
   * Track a notification click
   * @param   {Object} notification The notification object
   * @param   {Object} params The tracking params
   * @returns {undefined}
   */
  trackNotificationClick(notification) {
    trackNotificationClick(notification);
  }

  /**
   * creates the notification params for tracking purposes
   * @param {Object} notification - the notification object
   * @return {Object} params - params for tracking
   */
  createNotificationParams(notification) {
    return createNotificationParams(notification);
  }
}

const proNotifications = new ProNotifications();
export default proNotifications;
